import React, { useEffect, useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import {
  ContractDetailsFields,
  CoverageFields,
  BillingInformationFields,
  IncreaseDetailsFields,
} from './maintenanceContractFields';
import useMaintenanceContractFormLogic from './useMaintenanceContractFormLogic';
import VirtualizedAutocomplete from '../../../../components/Dropdown/VirtualizedAutocomplete';
import API_BASE_URL from '../../../../Constants';

const EditMaintenanceContractModal = ({
  open,
  handleClose,
  contractId,
  refreshContracts,
}) => {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buildingLabel, setBuildingLabel] = useState("N/A");
  const { formData, handleInputChange, options } = useMaintenanceContractFormLogic(open, contractId);

  // Fetch contract details when the modal opens
  useEffect(() => {
    const fetchContract = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/maintenance/maintenance-contracts/${contractId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Error fetching contract');
        }

        const data = await response.json();
        setContract(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching contract:', error);
        setContract(null);
        setLoading(false);
      }
    };


    if (open && contractId) {
      fetchContract();
    }
  }, [open, contractId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation (you can enhance this as needed)
    const requiredFields = [
      ...ContractDetailsFields,
      ...CoverageFields,
      ...BillingInformationFields,
      // Add IncreaseDetailsFields if required
    ].filter((field) => field.required);

    for (let field of requiredFields) {
      if (!formData[field.name]) {
        alert(`Please fill in the ${field.label} field.`);
        return;
      }
    }

    try {
      await fetch(`${API_BASE_URL}/api/maintenance/maintenance-contracts/${contractId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include',
      });

      alert('Maintenance contract updated successfully!');
      handleClose();
      if (refreshContracts) refreshContracts();
    } catch (error) {
      console.error('Error updating maintenance contract:', error);
      alert('Failed to update maintenance contract. Please try again.');
    }

  };

  // Render a single field
  const renderField = (field) => {
    switch (field.type) {
      case 'select':
        return (
          <div key={field.name} className="flex flex-col mb-4 w-full">
            <label className="mb-1 font-semibold text-sm text-gray-700">
              {field.label}
              {field.required && ' *'}
            </label>
            <select
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleInputChange}
              required={field.required}
              className="border border-gray-300 rounded-md p-2"
            >
              <option value="">Select {field.label}</option>
              {options[field.name]?.map((option) => (
                <option key={option[field.optionValue]} value={option[field.optionValue]}>
                  {option[field.optionLabel]}
                </option>
              ))}
            </select>
          </div>
        );
      case 'text':
      case 'number':
      case 'date':
        return (
          <div key={field.name} className="flex flex-col mb-4 w-full">
            <label className="mb-1 font-semibold text-sm text-gray-700">
              {field.label}
              {field.required && ' *'}
            </label>
            <input
              type={field.type}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleInputChange}
              required={field.required}
              className="border border-gray-300 rounded-md p-2"
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle className="text-2xl font-bold">Edit Maintenance Contract</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : contract ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contract Details */}
              <div className="border border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4">Contract Details</h3>

                <label htmlFor="buildingId" className="flex flex-col mb-4 w-full">
                  <span className="mb-1 font-semibold text-sm text-gray-700">
                    Building Site *
                  </span>
                  <VirtualizedAutocomplete
                    options={options.buildingId || []}
                    value={{ buildingId: formData["buildingId"], buildingLabel: buildingLabel }}
                    disabled={false}
                    name="buildingId"
                    onChange={(e, newValue) => {

                      if (newValue) {
                        handleInputChange({ target: { name: "buildingId", value: newValue.buildingID } });
                        setBuildingLabel(`${newValue.buildingID} - ${newValue.buildingReference}`);

                      } else {
                        handleInputChange({ target: { name: "buildingId", value: null } });
                        setBuildingLabel(null);

                      }
                    }}

                    optionKey="buildingId"
                    optionLabel="buildingLabel"


                    renderOption={(props, option, state) => (
                      <li {...props} key={option.buildingID} value={option.buildingID} >
                        {option.buildingID} - {option.buildingReference}
                      </li>
                    )}


                    filterOptions={(options, state) => {
                      const inputValue = state.inputValue.toLowerCase();
                      if (inputValue.trim() === '' || inputValue === null) { return options }
                      return options.filter((option) =>
                        (String(option.buildingID) || '').toLowerCase().includes(inputValue) ||
                        (option.buildingReference || '').toString().toLowerCase().includes(inputValue)
                      );
                    }}

                  />

                </label>

                {ContractDetailsFields.map(renderField)}
              </div>

              {/* Coverage */}
              <div className="border border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4">Coverage</h3>
                {CoverageFields.map(renderField)}
              </div>

              {/* Billing Information */}
              <div className="border border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4">Billing Information</h3>
                {BillingInformationFields.map(renderField)}
              </div>

              {/* Increase Details */}
              <div className="border border-gray-300 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4">Increase Details</h3>
                {IncreaseDetailsFields.map(renderField)}
              </div>
            </div>
          ) : (
            <Typography>No contract details available.</Typography>
          )}
        </DialogContent>
        <DialogActions className="justify-end p-4">
          <Button onClick={handleClose} color="secondary" variant="outlined" className="mr-2">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditMaintenanceContractModal;
